import { useMemo, useState } from 'react'
import { Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import {
  isCurrentUserAdminOrGlobalAdminSelector,
  useAddGroupMembersMutation,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupMembersQuery,
  useGetGroupsQuery,
  useGetUsersQuery,
  useRemoveGroupMemberMutation,
  useUpdateGroupMutation,
  type EntitiesGroupResponse
} from '@axilon-apps/domains-identity/state'
import { useAppSelector } from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table'
import { Textarea } from '../../../components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'

function errorStatus(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    return (error as { status?: number }).status
  }
  return undefined
}

function ErrorMessage({ error }: { error: unknown }) {
  const status = errorStatus(error)
  const message =
    status === 403
      ? 'You are not allowed to manage groups.'
      : status === 409
        ? 'A group with this name already exists.'
        : status === 412
          ? 'This group changed elsewhere. Reload it and retry.'
          : 'The group request failed. Please try again.'
  return (
    <p role="alert" className="text-sm text-st-down">
      {message}
    </p>
  )
}

export function GroupsTab() {
  const isAdmin = useAppSelector(isCurrentUserAdminOrGlobalAdminSelector)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<EntitiesGroupResponse | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [memberIds, setMemberIds] = useState<string[]>([])
  const [memberSearch, setMemberSearch] = useState('')

  const groups = useGetGroupsQuery({ page, perPage: 25 }, { skip: !isAdmin })
  const members = useGetGroupMembersQuery(editing?.id ?? '', {
    skip: !editing || !isAdmin
  })
  const users = useGetUsersQuery({ page: 1, perPage: 100 }, { skip: !isAdmin })
  const [createGroup, createState] = useCreateGroupMutation()
  const [updateGroup, updateState] = useUpdateGroupMutation()
  const [deleteGroup] = useDeleteGroupMutation()
  const [addMembers, addMembersState] = useAddGroupMembersMutation()
  const [removeMember] = useRemoveGroupMemberMutation()

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return groups.data?.items ?? []
    return (groups.data?.items ?? []).filter(
      (group) =>
        group.name.toLowerCase().includes(query) || group.description.toLowerCase().includes(query)
    )
  }, [groups.data?.items, search])

  const existingMemberIds = useMemo(
    () => new Set((members.data ?? []).map((member) => member.userId)),
    [members.data]
  )
  const availableUsers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase()
    return (users.data?.items ?? [])
      .filter((user) => !existingMemberIds.has(user.id))
      .filter(
        (user) =>
          !query ||
          user.name.toLowerCase().includes(query) ||
          user.username.toLowerCase().includes(query)
      )
  }, [existingMemberIds, memberSearch, users.data?.items])

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-5 text-sm text-ink-3">
          Groups are available to administrators only.
        </CardContent>
      </Card>
    )
  }

  const openForm = (group?: EntitiesGroupResponse) => {
    setEditing(group ?? null)
    setName(group?.name ?? '')
    setDescription(group?.description ?? '')
    setMemberIds([])
    setMemberSearch('')
    setFormOpen(true)
  }

  const closeForm = () => {
    setEditing(null)
    setName('')
    setDescription('')
    setFormOpen(false)
  }

  const save = async () => {
    const trimmedName = name.trim()
    if (!trimmedName || trimmedName.length > 128 || description.length > 1024) return

    if (editing) {
      await updateGroup({
        id: editing.id,
        entitiesUpdateGroupRequest: { name: trimmedName, description }
      }).unwrap()
    } else {
      await createGroup({ name: trimmedName, description }).unwrap()
    }
    await groups.refetch()
    closeForm()
  }

  const addSelected = async () => {
    if (!editing || memberIds.length === 0) return
    await addMembers({
      id: editing.id,
      entitiesAddGroupMembersRequest: {
        userIds: memberIds.filter((id) => !existingMemberIds.has(id))
      }
    }).unwrap()
    setMemberIds([])
    await members.refetch()
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="gap-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Groups</CardTitle>
            <p className="mt-1 text-xs font-normal text-ink-3">
              Create groups and manage their members.
            </p>
          </div>
          <Button size="sm" onClick={() => openForm()}>
            <Plus className="size-3.5" /> New group
          </Button>
        </CardHeader>

        <CardContent className="flex flex-col gap-3">
          <label htmlFor="group-search" className="text-xs font-medium text-ink-2">
            Search groups
          </label>
          <Input
            id="group-search"
            placeholder="Search by name or description"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
          />

          {groups.error ? <ErrorMessage error={groups.error} /> : null}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Group</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-ink-3">
                    Loading groups…
                  </TableCell>
                </TableRow>
              ) : visibleGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-ink-3">
                    {search
                      ? 'No groups match your search.'
                      : 'No groups yet. Create the first one.'}
                  </TableCell>
                </TableRow>
              ) : (
                visibleGroups.map((group) => (
                  <TableRow
                    key={group.id}
                    className="cursor-pointer"
                    onClick={() => openForm(group)}
                  >
                    <TableCell className="font-medium">{group.name}</TableCell>
                    <TableCell className="max-w-0 truncate text-ink-3">
                      {group.description || 'No description'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`Edit ${group.name}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            openForm(group)
                          }}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`Delete ${group.name}`}
                          onClick={async (event) => {
                            event.stopPropagation()
                            if (
                              window.confirm(
                                `Delete ${group.name}? This removes all memberships; resource grants referencing this group stop applying immediately.`
                              )
                            ) {
                              await deleteGroup(group.id).unwrap()
                              await groups.refetch()
                            }
                          }}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {groups.data && groups.data.pages > 1 ? (
            <div className="flex items-center justify-between text-xs text-ink-3">
              <span>
                Page {groups.data.page} of {groups.data.pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="xs"
                  disabled={!groups.data.hasPrev}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={!groups.data.hasNext}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={(open) => (open ? setFormOpen(true) : closeForm())}>
        <DialogContent className="max-h-[calc(100vh-2rem)] w-[52rem] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[52rem]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit group' : 'Create group'}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue={editing ? 'members' : 'details'}>
            {editing && (
              <TabsList variant="line" className="border-b border-border p-0">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="members">Members</TabsTrigger>
              </TabsList>
            )}
            <TabsContent value="details" className="pt-2">
              <div className="flex flex-col gap-3">
                <label htmlFor="group-name" className="text-xs font-medium text-ink-2">
                  Name
                </label>
                <Input
                  id="group-name"
                  maxLength={128}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
                <label htmlFor="group-description" className="text-xs font-medium text-ink-2">
                  Description <span className="font-normal text-ink-3">(optional)</span>
                </label>
                <Textarea
                  id="group-description"
                  maxLength={1024}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={3}
                />
                {createState.error ? <ErrorMessage error={createState.error} /> : null}
                {updateState.error ? <ErrorMessage error={updateState.error} /> : null}
                <DialogFooter>
                  <Button variant="outline" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => void save()}
                    disabled={
                      !name.trim() ||
                      name.trim().length > 128 ||
                      description.length > 1024 ||
                      createState.isLoading ||
                      updateState.isLoading
                    }
                  >
                    Save group
                  </Button>
                </DialogFooter>
              </div>
            </TabsContent>
            {editing && (
              <TabsContent value="members" className="pt-2">
                <div className="grid gap-5 md:grid-cols-2">
                  {members.error ? <ErrorMessage error={members.error} /> : null}
                  <section aria-labelledby="current-members-heading" className="min-w-0">
                    <h3
                      id="current-members-heading"
                      className="mb-2 text-xs font-medium text-ink-2"
                    >
                      Current members
                    </h3>
                    <div className="flex max-h-72 flex-col overflow-y-auto rounded-md border px-3">
                      {(members.data ?? []).length === 0 ? (
                        <p className="py-3 text-sm text-ink-3">No members yet.</p>
                      ) : (
                        (members.data ?? []).map((member) => (
                          <div
                            key={member.userId}
                            className="flex items-center gap-2 border-t py-2 text-sm first:border-t-0"
                          >
                            <span className="min-w-0 flex-1">{member.name || member.username}</span>
                            {!member.enabled ? <Badge variant="secondary">disabled</Badge> : null}
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              aria-label={`Remove ${member.username}`}
                              onClick={async () => {
                                await removeMember({
                                  id: editing.id,
                                  userId: member.userId
                                }).unwrap()
                                await members.refetch()
                              }}
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </section>

                  <section
                    className="min-w-0 rounded-md border p-3"
                    aria-labelledby="add-members-heading"
                  >
                    <h3 id="add-members-heading" className="mb-3 text-xs font-medium text-ink-2">
                      Add members
                    </h3>
                    <div className="mb-3 flex items-center gap-2 rounded-md border px-2.5">
                      <Search className="size-3.5 shrink-0 text-ink-3" />
                      <Input
                        aria-label="Search users to add"
                        className="h-8 border-0 px-1 shadow-none focus-visible:ring-0"
                        placeholder="Search users"
                        value={memberSearch}
                        onChange={(event) => setMemberSearch(event.target.value)}
                      />
                      {memberSearch ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          aria-label="Clear user search"
                          onClick={() => setMemberSearch('')}
                        >
                          <X />
                        </Button>
                      ) : null}
                    </div>
                    <div className="mb-2 flex items-center justify-between text-xs text-ink-3">
                      <span>
                        {memberIds.length ? `${memberIds.length} selected` : 'Select users to add'}
                      </span>
                      {memberIds.length ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          className="h-auto px-1.5 py-0.5"
                          onClick={() => setMemberIds([])}
                        >
                          Clear selection
                        </Button>
                      ) : null}
                    </div>
                    <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
                      {availableUsers.map((user) => (
                        <label
                          key={user.id}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/60"
                        >
                          <input
                            type="checkbox"
                            checked={memberIds.includes(user.id)}
                            onChange={(event) =>
                              setMemberIds((current) =>
                                event.target.checked
                                  ? [...current, user.id]
                                  : current.filter((id) => id !== user.id)
                              )
                            }
                          />
                          <span className="min-w-0 flex-1 truncate">
                            {user.name || user.username}
                            {user.name && user.username !== user.name ? (
                              <span className="ml-1 text-xs text-ink-3">({user.username})</span>
                            ) : null}
                          </span>
                          {!user.enabled ? <Badge variant="secondary">disabled</Badge> : null}
                        </label>
                      ))}
                      {availableUsers.length === 0 ? (
                        <p className="px-2 py-3 text-sm text-ink-3">
                          {memberSearch ? 'No matching users.' : 'All users are already members.'}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      className="mt-3"
                      size="sm"
                      onClick={() => void addSelected()}
                      disabled={memberIds.length === 0 || addMembersState.isLoading}
                    >
                      Add selected members
                    </Button>
                  </section>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  )
}
